import { baseUrl } from '../constant/constant';
import { getDeviceFingerprint } from './deviceIntelligence';
import {
    startBehavioralCapture,
    stopBehavioralCapture,
    setTelemetryTokenGetter,
    recordBehaviorEvent,
} from './behavioralAnalytics';

/**
 * Layers 1 & 7 (client side) — open a fraud session, feed the decision engine,
 * and run OTP challenges. Every call is best-effort: on any failure it resolves
 * to null / a safe default so checkout is never blocked by the fraud call
 * itself.
 */

const SESSION_KEY = 'ap_checkout_session';

// Last contact pair pushed to the server, so repeated calls (every render of the
// contact step, plus the payment modal's own start call) don't re-hit
// /fraud/validate-contact — which does an MX lookup — for an unchanged email.
let lastSyncedContact = '';

// Fraud-prevention is advisory: it must NEVER wedge checkout. Every network
// call is bounded so a slow/hung/unreachable endpoint fails open (resolves to a
// safe default) instead of leaving the UI stuck on "Checking…".
const FRAUD_FETCH_TIMEOUT_MS = 4000;

async function fraudFetch(url, options = {}, timeoutMs = FRAUD_FETCH_TIMEOUT_MS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

export function getCheckoutSessionToken() {
    try {
        return sessionStorage.getItem(SESSION_KEY) || '';
    } catch (_) {
        return '';
    }
}

function setCheckoutSessionToken(token) {
    try {
        if (token) sessionStorage.setItem(SESSION_KEY, token);
    } catch (_) { /* ignore */ }
}

// Let the behavioural layer read the current token without an import cycle.
setTelemetryTokenGetter(getCheckoutSessionToken);

export function clearCheckoutSession() {
    stopBehavioralCapture('abandon');
    lastSyncedContact = '';
    try {
        sessionStorage.removeItem(SESSION_KEY);
    } catch (_) { /* ignore */ }
}

// Guards against duplicate session creation. Once a token exists we reuse it for
// the rest of the checkout flow; while a /fraud/start request is in flight we
// hand every caller the same promise. Together these stop the ticket-select page
// and the payment page (and React StrictMode's double-invoke) from each opening
// their own session row.
let startInFlight = null;

/**
 * Layers 1-3 — open a fraud session and start behavioural capture, or reuse the
 * one already open for this checkout. Returns the server payload
 * ({ session_token, risk_band, blocked, ... }) or null on error.
 */
export async function startCheckoutSession(payload = {}) {
    // Already have a session for this checkout — reuse it, don't open another.
    // (Capture was started alongside the token and is still running; calling
    // startBehavioralCapture() again here would spuriously log a restart.)
    const existingToken = getCheckoutSessionToken();
    if (existingToken) {
        // The session is opened BEFORE the buyer reaches the contact step, so a
        // guest's email/phone are still empty at that point. A later caller
        // (the payment modal) does know them — push them onto the existing
        // session instead of discarding them with the short-circuit, otherwise
        // fraud_sessions.email stays NULL for every guest checkout.
        syncCheckoutContact(payload);
        return { session_token: existingToken, risk_band: 'approve', blocked: false, reused: true };
    }

    // Collapse concurrent callers onto a single request so only one row is made.
    if (startInFlight) return startInFlight;

    startInFlight = (async () => {
        try {
            const res = await fraudFetch(`${baseUrl}/fraud/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...payload,
                    device_fingerprint: getDeviceFingerprint(),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }),
            });
            const data = await res.json().catch(() => ({}));
            // Only cache the token for a session that may proceed. A BLOCKED
            // session must not be cached — otherwise the reuse short-circuit
            // above would hand the next checkout `blocked:false` off the stale
            // token and let a blacklisted buyer straight in. Not caching it means
            // their next attempt re-hits /fraud/start and is blocked again.
            if (data?.session_token && !data?.blocked) {
                setCheckoutSessionToken(data.session_token);
                startBehavioralCapture();
            }
            return data || null;
        } catch (_) {
            return null;
        } finally {
            startInFlight = null;
        }
    })();

    return startInFlight;
}

/**
 * Layers 4-7 — ask the server for the pre-payment decision. Returns
 * { decision, challenge_type, message } (decision: approve|challenge|decline)
 * or null on error (treat null as approve).
 */
export async function assessCheckout(extra = {}) {
    const session_token = getCheckoutSessionToken();
    if (!session_token) return null;
    try {
        const res = await fraudFetch(`${baseUrl}/fraud/assess`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token, ...extra }),
        });
        return await res.json().catch(() => null);
    } catch (_) {
        return null;
    }
}

export async function sendCheckoutOtp(phone) {
    const session_token = getCheckoutSessionToken();
    if (!session_token) return { status: 'failed', message: 'No active session.' };
    try {
        const res = await fetch(`${baseUrl}/fraud/send-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token, phone }),
        });
        return await res.json().catch(() => ({ status: 'failed' }));
    } catch (_) {
        return { status: 'failed', message: 'Could not send code.' };
    }
}

export async function verifyCheckoutOtp(code) {
    const session_token = getCheckoutSessionToken();
    if (!session_token) return { status: 'failed', verified: false };
    try {
        const res = await fetch(`${baseUrl}/fraud/verify-otp`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token, code }),
        });
        return await res.json().catch(() => ({ status: 'failed', verified: false }));
    } catch (_) {
        return { status: 'failed', verified: false };
    }
}

/**
 * Layer 3 — attach the buyer's email/phone to the open fraud session.
 *
 * The session row is created when the buyer leaves ticket selection, which is
 * BEFORE a guest has typed anything: `email` is empty at that point and no
 * later call updated it, so fraud_sessions.email stayed NULL for guests and the
 * device profile's distinct_emails never moved for a checkout that didn't reach
 * a charge. This pushes the contact details onto the session as soon as they're
 * known. Best-effort and fire-and-forget — never blocks or fails checkout.
 *
 * Returns the validation payload ({ valid, deliverable, disposable, suggestion })
 * when a request was made, otherwise null.
 */
export async function syncCheckoutContact({ email, phone } = {}) {
    const session_token = getCheckoutSessionToken();
    if (!session_token) return null;

    // The endpoint requires a well-formed address; a half-typed one would just
    // 422. Cheap shape check, the server re-validates.
    const address = String(email || '').trim().toLowerCase();
    if (!address || !/^\S+@\S+\.\S+$/.test(address)) return null;

    const key = `${session_token}|${address}|${String(phone || '').trim()}`;
    if (key === lastSyncedContact) return null;
    lastSyncedContact = key;

    try {
        const res = await fraudFetch(`${baseUrl}/fraud/validate-contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token, email: address, phone: phone || undefined }),
        });
        return await res.json().catch(() => null);
    } catch (_) {
        // Let a failed sync be retried rather than sticking on the cached key.
        lastSyncedContact = '';
        return null;
    }
}

/** Note a coupon attempt so the behavioural layer can flag coupon testing. */
export function noteCouponAttempt() {
    recordBehaviorEvent('coupon_attempt');
}

/** Common fraud fields to merge into every payment request body. */
export function fraudFields() {
    return {
        device_fingerprint: getDeviceFingerprint(),
        session_token: getCheckoutSessionToken(),
    };
}
